// The instrument strip that sits on top of every admin view: KillSwitch
// state, the balance it guards and how much of its funds the provider has
// spent, RunPod's own balance and burn rate, what is live there, and the
// queued / running task counts.
//
// It is in the shell rather than on a page because these numbers are the
// ones an operator should never have to navigate to see. When the switch
// is tripped the whole strip turns red, so "are we stopped?" is answered by
// the top edge of the screen before anything is read.
import { Link } from "react-router-dom";
import {
  ageLabel,
  ageSeconds,
  budgetUsedFraction,
  countLabel,
  fundsTotal,
  KILLSWITCH_STALE_S,
  killSwitchLabel,
  providerSpend,
  type AdminStatus,
} from "../../lib/adminStatus";
import { formatMinor } from "../../lib/format";

export default function AdminHud({ status }: { status: AdminStatus }) {
  const ks = status.killswitch;
  const label = killSwitchLabel(ks);
  const tripped = Boolean(ks?.tripped);
  const fraction = budgetUsedFraction(ks);
  const spent = providerSpend(ks);
  const funds = fundsTotal(ks);
  // The balance is computed from inputs KillSwitch re-reads every 30 s
  // while armed; say how old they are, and flag them once they are stale.
  const inputsAge = ageSeconds(ks?.inputs?.last_runpod_at);
  const stale = ks !== null && (inputsAge === undefined || inputsAge > KILLSWITCH_STALE_S);
  const runpod = ks?.providers?.runpod;
  const live = ks?.live_resources?.runpod;

  return (
    <div className={tripped ? "a-hud a-hud--tripped" : "a-hud"}>
      <div className="a-hud__cell">
        <span className="a-hud__label">KillSwitch</span>
        <span className="a-hud__value">
          <span className="a-hud__state">
            <span
              className={
                "a-lamp " +
                (label.tone === "bad" ? "a-lamp--red" : label.tone === "ok" ? "a-lamp--green" : "a-lamp--amber")
              }
            />
            <span className={label.tone === "bad" ? "a-hud__value--red" : "a-hud__value--green"}>{label.text}</span>
          </span>
        </span>
      </div>

      <div className="a-hud__cell" title="budget + billed − provider spend; KillSwitch trips below 0">
        <span className="a-hud__label">Balance</span>
        <span className="a-hud__value">
          {ks?.balance_minor === undefined ? "···" : formatMinor(ks.balance_minor, "USD_CENTS")}
        </span>
        {ks && (
          <span className={"a-hud__sub" + (stale ? " a-hud__sub--warn" : "")} data-testid="hud-balance-age">
            {stale && "stale · "}
            {ageLabel(inputsAge)}
          </span>
        )}
      </div>

      <div className="a-hud__cell" title="provider spend of the funds KillSwitch allows (budget + billed)">
        <span className="a-hud__label">Spent</span>
        <span className="a-hud__value a-hud__value--sm" data-testid="hud-spent">
          {spent === undefined || funds === undefined
            ? "···"
            : `${formatMinor(spent, "USD_CENTS")} / ${formatMinor(funds, "USD_CENTS")}`}
        </span>
        <span className={"a-gauge" + (tripped ? " a-gauge--red" : fraction !== undefined && fraction > 0.75 ? " a-gauge--amber" : "")}>
          <span style={{ width: `${Math.round((fraction ?? 0) * 100)}%` }} />
        </span>
      </div>

      <div className="a-hud__cell" title="RunPod's own account balance and current burn rate, from KillSwitch's last read">
        <span className="a-hud__label">RunPod</span>
        <span className="a-hud__value a-hud__value--sm" data-testid="hud-runpod">
          {runpod ? `${formatMinor(runpod.balance_minor, "USD_CENTS")} · ${formatMinor(runpod.spend_per_hr_minor, "USD_CENTS")}/h` : "···"}
        </span>
      </div>

      <div className="a-hud__cell" title="our pods plus running serverless workers, counted at RunPod by KillSwitch">
        <span className="a-hud__label">Live at RunPod</span>
        <span className={"a-hud__value" + (live ? " a-hud__value--amber" : "")} data-testid="hud-live">
          {live === undefined || live === null ? "···" : live}
        </span>
      </div>

      <div className="a-hud__cell">
        <span className="a-hud__label">Queued</span>
        <span className="a-hud__value a-hud__value--amber">{countLabel(status.counts?.queued)}</span>
      </div>

      <div className="a-hud__cell">
        <span className="a-hud__label">Running</span>
        <span className="a-hud__value a-hud__value--cyan">{countLabel(status.counts?.running)}</span>
      </div>

      {/* Queued and Running count tasks, not jobs. */}
      <div className="a-hud__cell a-hud__cell--grow">
        <span className="a-hud__label">Tasks, by state</span>
        <span className="a-hud__value a-hud__value--sm a-hud__value--dim">
          {status.error
            ? status.error
            : status.updatedAt
              ? `updated ${new Date(status.updatedAt).toLocaleTimeString()}`
              : "contacting control plane…"}
        </span>
      </div>

      <div className="a-hud__cell a-hud__cell--acts">
        <button className="a-btn" onClick={status.refresh} title="re-read now">
          Refresh
        </button>
        <Link className="a-btn" to="/">
          Studio →
        </Link>
      </div>
    </div>
  );
}
